// Revenue-goal View - onboarding step (after modality) where the creator sets
// their 12-month revenue target on a discrete 5-position slider. Selection state
// lives in useRevenueGoalViewModel; the parent persists the value via onSubmit.

import { PlaybookLogo } from '@design-system/components/logo/PlaybookLogo';
import { Slider } from '@design-system/components/input/slider';
import {
  MAX_REVENUE_POSITION,
  MIN_REVENUE_POSITION,
  REVENUE_BRACKETS,
  useRevenueGoalViewModel,
} from '../view-model/RevenueGoal.viewModel';
import * as styles from './RevenueGoal.view.css';

type RevenueGoalViewProps = {
  initialPosition: number;
  onSubmit: (position: number) => void;
  onBack: () => void;
  canGoBack: boolean;
};

export const RevenueGoalView = ({
  initialPosition,
  onSubmit,
  onBack,
  canGoBack,
}: RevenueGoalViewProps) => {
  const vm = useRevenueGoalViewModel(initialPosition);
  const { state } = vm;

  return (
    <div className={styles.pageContainer} data-testid="revenue-goal-view">
      <header className={styles.topHeader}>
        <PlaybookLogo showGlyph showName height={24} color="N-100" />
      </header>

      <div className={styles.content}>
        {canGoBack ? (
          <div className={styles.backRow}>
            <button
              type="button"
              className={styles.backButton}
              onClick={onBack}
              data-testid="back-button"
              aria-label="Go back"
            >
              <span className={styles.backIcon}>←</span>
            </button>
          </div>
        ) : null}

        <h1 className={styles.title}>
          What&apos;s your revenue target from Playbook in the next 12 months?
        </h1>

        <div className={styles.valueLabel} aria-live="polite">
          <p className={styles.amount} data-testid="revenue-goal-amount">
            {state.bracket.amount}
          </p>
        </div>

        <Slider
          min={MIN_REVENUE_POSITION}
          max={MAX_REVENUE_POSITION}
          value={state.position}
          onChange={vm.lazyDispatch('SetPosition')}
          ariaLabel="Revenue target for the next 12 months"
          ariaValueText={state.bracket.amount}
        />

        <div className={styles.bracketLabels} aria-hidden>
          {REVENUE_BRACKETS.map((bracket) => (
            <span key={bracket.position} className={styles.bracketLabel}>
              {bracket.amount}
            </span>
          ))}
        </div>

        <button
          type="button"
          className={styles.continueButton}
          onClick={() => onSubmit(state.position)}
          data-testid="revenue-goal-continue"
        >
          Continue
        </button>
      </div>
    </div>
  );
};
